/**
 * The text the server read out of an attachment (Backend/lib/ocr.js), as the
 * screen draws it.
 *
 * The server stores plain lines with tables as Markdown (`| a | b |`); this
 * splits that into paragraphs and tables, picks each table's direction from
 * its script, and says what a status means to the person looking at the card.
 *
 * Pure: no React.
 */

const ARABIC = /[؀-ۿ]/;
const LATIN = /[A-Za-z]/;

/** 'rtl' when Arabic letters outnumber Latin ones, else 'ltr'. */
export function directionOf(text) {
  const value = String(text || '');
  const ar = (value.match(/[؀-ۿ]/g) || []).length;
  const en = (value.match(/[A-Za-z]/g) || []).length;
  return ar >= en && ar > 0 ? 'rtl' : 'ltr';
}

const isTableLine = (line) => /^\s*\|.*\|\s*$/.test(line);
const isRule = (line) => /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/.test(line);
const cellsOf = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

/**
 * [{ type: 'text', text, dir }, { type: 'table', header, rows, dir }]
 */
export function ocrBlocks(text) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let paragraph = [];
  const flush = () => {
    const body = paragraph.join('\n').trim();
    if (body) blocks.push({ type: 'text', text: body, dir: directionOf(body) });
    paragraph = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    if (isTableLine(lines[i]) && i + 1 < lines.length && isRule(lines[i + 1])) {
      flush();
      const header = cellsOf(lines[i]);
      const rows = [];
      i += 2;
      while (i < lines.length && isTableLine(lines[i])) {
        rows.push(cellsOf(lines[i]));
        i += 1;
      }
      i -= 1;
      const all = [header, ...rows].flat().join(' ');
      blocks.push({ type: 'table', header, rows, dir: directionOf(all) });
    } else if (!lines[i].trim()) {
      flush();
    } else {
      paragraph.push(lines[i]);
    }
  }
  flush();
  return blocks;
}

/** What each status says on the card. */
export const OCR_STATUS_META = Object.freeze({
  pending: { label: 'بانتظار قراءة النص', icon: 'scan-text', tone: 'sky', busy: true },
  processing: { label: 'جارٍ استخراج النص…', icon: 'loader-2', tone: 'sky', busy: true },
  done: { label: 'تم استخراج النص', icon: 'scan-text', tone: 'emerald', busy: false },
  empty: { label: 'لا يوجد نص في الصورة', icon: 'scan-line', tone: 'slate', busy: false },
  failed: { label: 'تعذرت قراءة النص', icon: 'alert-triangle', tone: 'amber', busy: false },
});

/** Is any row of a list still being read — worth polling for? */
export const ocrInFlight = (rows) => (rows || []).some((r) => r && (r.ocrStatus === 'pending' || r.ocrStatus === 'processing'));

/** The plain text, for the clipboard: tables as tab-separated rows. */
export function ocrPlainText(text) {
  return ocrBlocks(text)
    .map((block) =>
      block.type === 'table' ? [block.header, ...block.rows].map((row) => row.join('\t')).join('\n') : block.text,
    )
    .join('\n\n');
}

export const hasArabic = (text) => ARABIC.test(String(text || ''));
export const hasLatin = (text) => LATIN.test(String(text || ''));
